import axios from "axios";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useAuth } from "./AuthContext";

export interface CaseCategory {
  id: string;
  name: string;
  position: number;
  caseCount: number;
}

// "all" = every active case, "favorites" = starred cases, "category" = a
// specific user category (categoryId set).
export interface CategoryFilter {
  type: "all" | "favorites" | "category";
  categoryId: string | null;
}

// Custom drag MIME type used when dragging a case card onto a sidebar category.
export const CASE_DRAG_MIME = "application/x-pnai-case";

interface CaseCategoriesContextType {
  categories: CaseCategory[];
  favoritesCount: number;
  loading: boolean;
  activeFilter: CategoryFilter;
  setActiveFilter: (filter: CategoryFilter) => void;
  /** Bumped whenever an assignment/favorite changes so lists can reconcile. */
  mutationVersion: number;
  refresh: () => Promise<void>;
  createCategory: (name: string) => Promise<CaseCategory | null>;
  updateCategory: (id: string, updates: { name?: string }) => Promise<void>;
  deleteCategory: (id: string) => Promise<void>;
  reorderCategories: (orderedIds: string[]) => Promise<void>;
  addCaseToCategory: (caseId: string, categoryId: string) => Promise<void>;
  removeCaseFromCategory: (caseId: string, categoryId: string) => Promise<void>;
  setCaseCategories: (caseId: string, categoryIds: string[]) => Promise<void>;
  toggleFavorite: (caseId: string, favorite: boolean) => Promise<void>;
  discardCase: (caseId: string) => Promise<void>;
  /** True while a case card is being dragged — lets drop targets hint they're droppable. */
  isDraggingCase: boolean;
  setDraggingCase: (dragging: boolean) => void;
}

const noop = async () => {};

const CaseCategoriesContext = createContext<CaseCategoriesContextType>({
  categories: [],
  favoritesCount: 0,
  loading: false,
  activeFilter: { type: "all", categoryId: null },
  setActiveFilter: () => {},
  mutationVersion: 0,
  refresh: noop,
  createCategory: async () => null,
  updateCategory: noop,
  deleteCategory: noop,
  reorderCategories: noop,
  addCaseToCategory: noop,
  removeCaseFromCategory: noop,
  setCaseCategories: noop,
  toggleFavorite: noop,
  discardCase: noop,
  isDraggingCase: false,
  setDraggingCase: () => {},
});

export function CaseCategoriesProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [categories, setCategories] = useState<CaseCategory[]>([]);
  const [favoritesCount, setFavoritesCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [mutationVersion, setMutationVersion] = useState(0);
  const [isDraggingCase, setDraggingCase] = useState(false);
  const [activeFilter, setActiveFilter] = useState<CategoryFilter>({
    type: "all",
    categoryId: null,
  });

  const bump = useCallback(() => setMutationVersion((v) => v + 1), []);

  const refresh = useCallback(async () => {
    if (!user?.id) return;
    try {
      const res = await axios.get("/api/case-categories", {
        withCredentials: true,
      });
      setCategories(res.data.categories || []);
      setFavoritesCount(res.data.favoritesCount || 0);
    } catch (error) {
      console.error("Error fetching case categories:", error);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    refresh().finally(() => setLoading(false));
  }, [user?.id, refresh]);

  const createCategory = useCallback(
    async (name: string) => {
      try {
        const res = await axios.post(
          "/api/case-categories",
          { name },
          { withCredentials: true }
        );
        await refresh();
        return res.data.category as CaseCategory;
      } catch (error) {
        console.error("Error creating category:", error);
        return null;
      }
    },
    [refresh]
  );

  const updateCategory = useCallback(
    async (id: string, updates: { name?: string }) => {
      // Optimistic update for snappy renames.
      setCategories((prev) =>
        prev.map((c) => (c.id === id ? { ...c, ...updates } : c))
      );
      try {
        await axios.patch(`/api/case-categories/${id}`, updates, {
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error updating category:", error);
        await refresh();
      }
    },
    [refresh]
  );

  const deleteCategory = useCallback(
    async (id: string) => {
      setCategories((prev) => prev.filter((c) => c.id !== id));
      // If we were viewing the deleted category, fall back to "All cases".
      setActiveFilter((prev) =>
        prev.type === "category" && prev.categoryId === id
          ? { type: "all", categoryId: null }
          : prev
      );
      try {
        await axios.delete(`/api/case-categories/${id}`, {
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error deleting category:", error);
      } finally {
        await refresh();
        bump();
      }
    },
    [refresh, bump]
  );

  const reorderCategories = useCallback(
    async (orderedIds: string[]) => {
      setCategories((prev) => {
        const byId = new Map(prev.map((c) => [c.id, c]));
        const reordered = orderedIds
          .map((id, i) => {
            const c = byId.get(id);
            return c ? { ...c, position: i } : null;
          })
          .filter((c): c is CaseCategory => c !== null);
        return reordered;
      });
      try {
        await axios.put(
          "/api/case-categories/reorder",
          { orderedIds },
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Error reordering categories:", error);
        await refresh();
      }
    },
    [refresh]
  );

  const addCaseToCategory = useCallback(
    async (caseId: string, categoryId: string) => {
      try {
        await axios.post(
          `/api/cases/${caseId}/categories/${categoryId}`,
          {},
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Error adding case to category:", error);
      } finally {
        await refresh();
        bump();
      }
    },
    [refresh, bump]
  );

  const removeCaseFromCategory = useCallback(
    async (caseId: string, categoryId: string) => {
      try {
        await axios.delete(`/api/cases/${caseId}/categories/${categoryId}`, {
          withCredentials: true,
        });
      } catch (error) {
        console.error("Error removing case from category:", error);
      } finally {
        await refresh();
        bump();
      }
    },
    [refresh, bump]
  );

  const setCaseCategories = useCallback(
    async (caseId: string, categoryIds: string[]) => {
      try {
        await axios.put(
          `/api/cases/${caseId}/categories`,
          { categoryIds },
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Error setting case categories:", error);
      } finally {
        await refresh();
        bump();
      }
    },
    [refresh, bump]
  );

  const toggleFavorite = useCallback(
    async (caseId: string, favorite: boolean) => {
      try {
        await axios.put(
          `/api/cases/${caseId}/favorite`,
          { favorite },
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Error toggling favorite:", error);
      } finally {
        await refresh();
        bump();
      }
    },
    [refresh, bump]
  );

  // Soft-delete (move to trash). Refresh category counts (which exclude
  // discarded cases) and bump so the cases list drops it.
  const discardCase = useCallback(
    async (caseId: string) => {
      try {
        await axios.patch(
          `/api/cases/${caseId}/discard`,
          {},
          { withCredentials: true }
        );
      } catch (error) {
        console.error("Error discarding case:", error);
      } finally {
        await refresh();
        bump();
      }
    },
    [refresh, bump]
  );

  return (
    <CaseCategoriesContext.Provider
      value={{
        categories,
        favoritesCount,
        loading,
        activeFilter,
        setActiveFilter,
        mutationVersion,
        refresh,
        createCategory,
        updateCategory,
        deleteCategory,
        reorderCategories,
        addCaseToCategory,
        removeCaseFromCategory,
        setCaseCategories,
        toggleFavorite,
        discardCase,
        isDraggingCase,
        setDraggingCase,
      }}
    >
      {children}
    </CaseCategoriesContext.Provider>
  );
}

export function useCaseCategories() {
  return useContext(CaseCategoriesContext);
}
